import React, { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import * as Utils from '@services/utils'
import urlUtils from '@services/urlUtils'
import { useUser } from '@state/uses/users'
import { useDances } from '@state/uses/dances'
import { getAllDances } from '@state/actions/dances'
import { EditBtn, ListBtn } from '@components/Table/TableButtons'
import ExpandButton from '@components/Buttons/ExpandButton'
import PaginatedTable from '@components/Table/PaginatedTable'
import DanceExpandedRow from '@components/Dances/DanceExpandedRow'
import DanceStatusLabel from '@components/Dances/DanceStatusLabel'
import {
    completionDateFormat,
    existingDanceCompletionDateFormat,
} from '@services/dances'
import i18n from '@localization/i18n'

export const getFullNameFromArr = (artists) => {
    if (!Utils.isEmpty(artists)) {
        return artists.map((a) => a.first_name + ' ' + a.last_name).join(', ')
    }
}

export const getArtistsNames = (song, firstType, secondType) => {
    if (!Utils.isEmpty(song[firstType])) {
        return song[firstType].map((a) => a.heb_name).join(', ')
    }
    return song[secondType]
}

export default ({}) => {
    const filters = [
        {
            property: 'status',
            label: i18n.t('dances_table_filters.status'),
        },
        {
            property: 'choreographer',
            subProperty: ['first_name', 'last_name'],
            label: i18n.t('dances_table_filters.choreographer'),
        },
        {
            property: 'style',
            label: i18n.t('dances_table_filters.style'),
        },
        {
            property: 'performer',
            label: i18n.t('dances_table_filters.performer'),
        },
        {
            property: 'composer',
            label: i18n.t('dances_table_filters.composer'),
        },
        {
            property: 'lyricist',
            label: i18n.t('dances_table_filters.lyricist'),
        },
    ]

    const history = useHistory()
    const location = useLocation()
    const user = useUser()
    const searchByUser = location?.state?.searchByUser
        ? location.state.searchByUser
        : null
    const searchByOrderStratus = location?.state?.searchByOrderStratus
        ? location.state.searchByOrderStratus
        : null

    const activeFilter =
        user?.is_admin && searchByOrderStratus
            ? {
                  status: [
                      {
                          value: searchByOrderStratus.value,
                          label: searchByOrderStratus.label,
                      },
                  ],
              }
            : searchByUser
            ? {
                  'choreographer:first_name,last_name': [
                      {
                          value: searchByUser.id,
                          label:
                              searchByUser.first_name +
                              ' ' +
                              searchByUser.last_name,
                      },
                  ],
              }
            : null

    const goToEditDance = (dance) => {
        const originalPage =
            window.location.search?.split('page=')[1]?.split('&')[0] || 1
        urlUtils.setQueryParams({ originalPage })
        localStorage.setItem(
            '@markidim:filter_state',
            window.location.search?.slice(1)
        )
        history.push({
            pathname: '/dance',
            search: urlUtils.buildQueryParams({
                id: dance.id,
            }),
        })
    }

    const checkPermision = (dance) =>
        user.is_admin || user.user_id === dance.choreographer.id

    const getDanceAndSongNames = (dance) => {
        let result = dance.name
        if (dance.song.name !== result) {
            result = result + ' (' + dance.song.name + ')'
        }

        return result
    }

    const goToUserDetails = (user) => {
        const userFirstName = user?.first_name
        const userLastName = user?.last_name
        history.push({
            pathname: '/users',
            search: `search=${userFirstName}%20${userLastName}`,
        })
    }

    const columns = [
        {
            property: 'name',
            label: i18n.t('dances_table.dance_name'),
            align: 'start',
            minWidth: '56px',
            sortable: true,
            isMobileTileHeader: true,
            cell: (dance) => getDanceAndSongNames(dance),
        },
        {
            property: 'status',
            label: i18n.t('dances_table.status'),
            dataScope: 'column',
            align: 'center',
            minWidth: 'none',
            sortable: true,
            cell: (dance) => <DanceStatusLabel status={dance.status} />,
        },
        {
            property: 'choreographer',
            subProperty: ['first_name', 'last_name'],
            label: i18n.t('dances_table.choreographer'),
            align: 'start',
            minWidth: '64px',
            sortable: true,
            isMobileTileHeader: true,
        },
        {
            property: 'style',
            label: i18n.t('dances_table.style'),
            align: 'start',
            minWidth: '60px',
            sortable: true,
        },
        {
            property: 'performer',
            label: i18n.t('dances_table.performer'),
            align: 'start',
            minWidth: '128px',
            sortable: true,
        },
        {
            property: 'composer',
            label: i18n.t('dances_table.composer'),
            align: 'start',
            minWidth: 'none',
            sortable: true,
            cell: (dance) =>
                getArtistsNames(dance['song'], 'composers', 'composer'),
        },
        {
            property: 'lyricist',
            label: i18n.t('dances_table.lyricist'),
            align: 'start',
            minWidth: 'none',
            sortable: true,
            cell: (dance) =>
                getArtistsNames(dance['song'], 'lyricists', 'lyricist'),
        },
        {
            property: 'request_date',
            label: i18n.t('dances_table.request_date'),
            align: 'center',
            minWidth: '92px',
            sortable: true,
            cell: (dance) =>
                dance['status'] == 'קיים ריקוד'
                    ? Utils.formatDate(
                          dance['request_date'],
                          existingDanceCompletionDateFormat
                      )
                    : Utils.formatDate(
                          dance['request_date'],
                          completionDateFormat
                      ),
        },
        {
            property: 'song',
            subProperty: 'akum_id',
            label: i18n.t('dances_table.akum_id'),
            align: 'center',
            minWidth: '92px',
            sortable: true,
        },
        {
            property: 'edit',
            label: i18n.t('dances_table.edit'),
            align: 'center',
            minWidth: '0px',

            cell: (dance) =>
                checkPermision(dance) && (
                    <EditBtn onClick={() => goToEditDance(dance)} />
                ),
        },
        {
            property: 'goToSongdList',
            label: i18n.t('dances_table.user_details'),
            align: 'center',
            minWidth: 'none',
            hideOnExport: true,
            hidden: !user?.is_admin,
            cell: (dance) => (
                <ListBtn onClick={() => goToUserDetails(dance.choreographer)} />
            ),
        },
        {
            property: 'expand',
            label: '',
            dataScope: 'col',
            align: 'start',
            minWidth: '60px',
            cell: (row, col, onClick, expanded) => (
                <ExpandButton
                    onClick={onClick}
                    expanded={expanded}
                    closedText={i18n.t('table.show_more_details')}
                    expandedText={i18n.t('table.show_less_details')}
                />
            ),
        },
    ]

    if (!user) {
        return null
    }

    return (
        <PaginatedTable
            title={i18n.t('dances_table_filters.dances_title')}
            columns={columns}
            collection={'dances'}
            loadAction={getAllDances}
            filters={filters}
            useFunction={useDances}
            ExpandedRowComponent={DanceExpandedRow}
            activeFilter={activeFilter}
        />
    )
}
